import React, { useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Paper,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import dayjs from 'dayjs';
import 'dayjs/locale/pl';
import { useGetPrepaymentsByReservationQuery } from '../../Service/extendedInvoicesApi';
import { PrepaymentSettlementStatus } from '../../Types/invoicesTypes';
import { PrepaymentSettlementStatusLabels } from '../../Types/invoicesLabels';
import type { PrepaymentsListProps, PrepaymentRowData } from './PrepaymentsList.types';
import { extractApiErrorMessage } from '../../../../Utils/apiErrorUtils';

dayjs.locale('pl');

const PrepaymentsList: React.FC<PrepaymentsListProps> = ({
  reservationId,
  selectedPrepaymentIds,
  onSelectionChange,
  disabled = false,
}) => {
  const shouldIncludeSettled = selectedPrepaymentIds.length > 0;

  // Query for prepayments (only Pending by default)
  const {
    data: prepayments,
    isLoading,
    error,
  } = useGetPrepaymentsByReservationQuery(
    { reservationId: reservationId!, includeSettled: shouldIncludeSettled },
    { skip: !reservationId }
  );

  // Transform data to rows with selection state
  const rows: PrepaymentRowData[] = useMemo(() => {
    if (!prepayments) return [];

    const result = prepayments
      .filter((prepayment) => {
        const isSelected = selectedPrepaymentIds.includes(prepayment.id);
        const isSettled = prepayment.settlementStatus === PrepaymentSettlementStatus.Settled;

        return !isSettled || isSelected;
      })
      .map((prepayment) => {
        const isSettled = prepayment.settlementStatus === PrepaymentSettlementStatus.Settled;
        const isSelected = selectedPrepaymentIds.includes(prepayment.id);

        return {
          ...prepayment,
          isSelectable: !disabled && (!isSettled || isSelected),
          isSelected,
        };
      });

    return result;
  }, [prepayments, selectedPrepaymentIds, disabled]);

  // Calculate total for selected prepayments
  const selectedTotal = useMemo(() => {
    return rows
      .filter((row) => row.isSelected)
      .reduce((sum, row) => sum + (row.documentTotalAfterPrepayments ?? row.totalGross), 0);
  }, [rows]);

  // Handle row selection toggle
  const handleToggle = useCallback(
    (prepaymentId: number) => {
      const newSelection = selectedPrepaymentIds.includes(prepaymentId)
        ? selectedPrepaymentIds.filter((id) => id !== prepaymentId)
        : [...selectedPrepaymentIds, prepaymentId];

      onSelectionChange(newSelection);
    },
    [selectedPrepaymentIds, onSelectionChange]
  );

  // Handle select all / deselect all
  const handleToggleAll = useCallback(() => {
    if (!rows || rows.length === 0) {
      return;
    }

    const selectableIds = rows.filter((row) => row.isSelectable).map((row) => row.id);

    if (selectableIds.length === 0) {
      return;
    }

    if (selectedPrepaymentIds.length === selectableIds.length && selectableIds.length > 0) {
      // Deselect all
      onSelectionChange([]);
    } else {
      // Select all selectable
      onSelectionChange(selectableIds);
    }
  }, [rows, selectedPrepaymentIds, onSelectionChange]);

  // No reservation selected
  if (!reservationId) {
    return (
      <Box>
        <Typography variant="body2" color="text.secondary">
          Wybierz rezerwację, aby wyświetlić dostępne zaliczki.
        </Typography>
      </Box>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={3}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Alert severity="error">
        Błąd podczas ładowania zaliczek:{' '}
        {extractApiErrorMessage(error, 'Nieznany błąd')}
      </Alert>
    );
  }

  // No prepayments
  if (!prepayments || prepayments.length === 0) {
    return (
      <Alert severity="info">
        Brak dostępnych zaliczek do rozliczenia dla wybranej rezerwacji.
      </Alert>
    );
  }

  const allSelectableSelected =
    rows.filter((r) => r.isSelectable).length === selectedPrepaymentIds.length &&
    selectedPrepaymentIds.length > 0;

  return (
    <Box>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={selectedPrepaymentIds.length > 0 && !allSelectableSelected}
                  checked={allSelectableSelected}
                  onChange={handleToggleAll}
                  disabled={disabled || rows.filter((r) => r.isSelectable).length === 0}
                />
              </TableCell>
              <TableCell>Numer faktury</TableCell>
              <TableCell>Data wystawienia</TableCell>
              <TableCell align="right">Kwota brutto</TableCell>
              <TableCell>Status płatności</TableCell>
              <TableCell>Status rozliczenia</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow
                key={row.id}
                hover={row.isSelectable}
                selected={row.isSelected}
                sx={{
                  cursor: row.isSelectable ? 'pointer' : 'default',
                  opacity: row.isSelectable ? 1 : 0.6,
                }}
              >
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={row.isSelected}
                    disabled={!row.isSelectable}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleToggle(row.id);
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight={row.isSelected ? 600 : 400}>
                    {row.invoiceNumber}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {dayjs(row.issueDate).format('DD.MM.YYYY')}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Box display="flex" alignItems="center" justifyContent="flex-end" gap={0.5}>
                    <Typography variant="body2" fontWeight={row.isSelected ? 600 : 400}>
                      {(row.documentTotalAfterPrepayments ?? row.totalGross).toFixed(2)} {row.currency}
                    </Typography>
                    {row.hasActiveCorrection && (
                      <Chip label="Skorygowano" size="small" color="warning" />
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip
                    label={row.paymentStatus}
                    size="small"
                    color={row.paymentStatus === 'Paid' ? 'success' : 'default'}
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={
                      row.settlementStatus
                        ? PrepaymentSettlementStatusLabels[row.settlementStatus]
                        : 'Oczekuje'
                    }
                    size="small"
                    color={
                      row.settlementStatus === PrepaymentSettlementStatus.Settled
                        ? 'success'
                        : 'default'
                    }
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Summary */}
      {selectedPrepaymentIds.length > 0 && (
        <Box
          mt={2}
          p={2}
          sx={{
            backgroundColor: 'action.hover',
            borderRadius: 1,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="body2">
            Zaznaczono: <strong>{selectedPrepaymentIds.length}</strong> zalicz
            {selectedPrepaymentIds.length === 1
              ? 'kę'
              : selectedPrepaymentIds.length < 5
              ? 'ki'
              : 'ek'}
          </Typography>
          <Typography variant="h6" fontWeight={600}>
            Suma: {selectedTotal.toFixed(2)}{' '}
            {prepayments && prepayments.length > 0 ? prepayments[0].currency : 'PLN'}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default PrepaymentsList;
